import React from "react";
import EliteSpinner from "./EliteSpinner";
import DropdownTheme from "./DropdownTheme";
import Dropdown from "./Dropdown";
import "./Eval.css";
import "../fonts/Modenine-2OPd.ttf";
import { EvalProps, EvalState, ThemeOption } from "../types";

class Eval extends React.Component<EvalProps, EvalState> {
  private scrollContainerRef = React.createRef<HTMLDivElement>();

  constructor(props: EvalProps) {
    super(props);
    this.state = {
      code: this.props.code,
      language_id: this.props.language_id,
      token: this.props.token,
      evalOutput: this.props.evalOutput,
      evalOutputDB: this.props.evalOutputDB,
      memory: this.props.memory,
      time: this.props.time,
      loading: this.props.loading,
      sessionid: this.props.sessionid,
      mode: "plain",
      compileOutput: this.props.compileOut,
      evalExtend: this.props.evalExtend,
      errOutput: this.props.errOutput,
      flanT5Toggle: this.props.flanT5Toggle,
      anthropicToggle: this.props.anthropicToggle,
      PaLMToggle: this.props.PaLMToggle,
      GeminiToggle: this.props.GeminiToggle,
      GrokAIToggle: this.props.GrokAIToggle,
      theme: [
        {
          id: 0,
          mode: "plain",
          key: "theme",
          title: "Plain",
          selected: false,
        },
        {
          id: 1,
          mode: "terminal",
          key: "theme",
          title: "Terminal",
          selected: false,
        },
      ],
      currTheme: "Plain",
    };
    this.toggleTheme = this.toggleTheme.bind(this);
    this.runCodeEval = this.runCodeEval.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
  }

  handleWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
    // Allow the scroll container to handle wheel events
    const container = this.scrollContainerRef.current;
    if (container) {
      e.stopPropagation(); // Stop event from bubbling to parent elements
      // The browser will handle the actual scrolling
    }
  }

  componentDidMount(): void {
    const keysToGet = ["keyTheme", "mode", "currTheme", "compileOutput", "evalOutput", "evalOutputDB"];

    const stateUpdate = keysToGet.reduce((acc: Partial<EvalState>, key: string) => {
      acc[key as keyof EvalState] = sessionStorage.getItem(key) as any;
      return acc;
    }, {});

    this.setState(stateUpdate as EvalState);

    // Browser detection for Safari
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      document.body.classList.add('safari');
    }
  }

  static getDerivedStateFromProps(nextProps: EvalProps, prevState: EvalState): Partial<EvalState> | null {
    if (
      nextProps.code !== prevState.code ||
      nextProps.language_id !== prevState.language_id ||
      nextProps.token !== prevState.token ||
      nextProps.evalOutput !== prevState.evalOutput ||
      nextProps.evalOutputDB !== prevState.evalOutputDB ||
      nextProps.memory !== prevState.memory ||
      nextProps.time !== prevState.time ||
      nextProps.loading !== prevState.loading ||
      nextProps.sessionid !== prevState.sessionid ||
      nextProps.compileOut !== prevState.compileOutput ||
      nextProps.evalExtend !== prevState.evalExtend ||
      nextProps.errOutput !== prevState.errOutput ||
      nextProps.flanT5Toggle !== prevState.flanT5Toggle ||
      nextProps.anthropicToggle !== prevState.anthropicToggle ||
      nextProps.PaLMToggle !== prevState.PaLMToggle ||
      nextProps.GeminiToggle !== prevState.GeminiToggle ||
      nextProps.GrokAIToggle !== prevState.GrokAIToggle
    ) {
      return {
        code: nextProps.code,
        language_id: nextProps.language_id,
        token: nextProps.token,
        evalOutput: nextProps.evalOutput,
        evalOutputDB: nextProps.evalOutputDB,
        memory: nextProps.memory,
        time: nextProps.time,
        loading: nextProps.loading,
        sessionid: nextProps.sessionid,
        compileOutput: nextProps.compileOut,
        evalExtend: nextProps.evalExtend,
        errOutput: nextProps.errOutput,
        flanT5Toggle: nextProps.flanT5Toggle,
        anthropicToggle: nextProps.anthropicToggle,
        PaLMToggle: nextProps.PaLMToggle,
        GeminiToggle: nextProps.GeminiToggle,
        GrokAIToggle: nextProps.GrokAIToggle,
      };
    }
    return null;
  }

  onKeyUp = (keyName: string, e: React.KeyboardEvent, handle: any): void => {
    console.log("test:onKeyUp", e, handle);
    this.setState({
      output: `onKeyUp ${keyName}`,
    });
  }

  onKeyDown = (keyName: string, e: React.KeyboardEvent, handle: any): void => {
    console.log("test:onKeyDown", keyName, e, handle);
    this.setState({
      output: `onKeyDown ${keyName}`,
    });
    this.props.runCode();
  }

  toggleTheme = (id: number, key: string, mode: string): void => {
    let temp = [...this.state[key as keyof EvalState] as ThemeOption[]];
    temp[id].selected = !temp[id].selected;
    let newMode = temp[id];
    this.setState({
      [key]: temp,
      mode: newMode["mode"],
      currTheme: newMode["title"],
    } as any);
    sessionStorage.setItem("keyTheme", JSON.stringify(temp));
    sessionStorage.setItem("modeTheme", newMode["mode"]);
    sessionStorage.setItem("currTheme", newMode["title"]);
  }

  runCodeEval = (): void => {
    this.props.runCode();
  }

  renderOutput = (output: string | null): React.ReactElement => {
    // Only render output if it is not null, undefined, or the string 'null'
    const { mode } = this.state;
    const themeClass = mode === 'terminal' ? 'output-container-terminal' : 'output-container-plain';
    if (output === null || output === undefined || output === "null") {
      return <div className={`output-container ${themeClass}`}>Terminal output here.</div>;
    }
    return (
      <div className={`output-container ${themeClass}`}>
        {output}
      </div>
    );
  };

  renderWithScroll = (output: string | null): React.ReactElement => {
    return (
      <div className="eval-scroll-clip">
        <div
          className="scroll-container"
          ref={this.scrollContainerRef}
          onWheel={this.handleWheel}
          tabIndex={0}
        >
          {this.renderOutput(output)}
        </div>
      </div>
    );
  };

  render(): React.ReactElement {
    // Get output from props first, then fall back to sessionStorage
    const evalOutput = this.props.evalOutput || sessionStorage.getItem("evalOutput");
    const sessionOutput = sessionStorage.getItem("evalOutput");
    const loading = this.props.loading;
    const time = this.props.time;
    const memory = this.props.memory;
    const evalOutputDB = this.props.evalOutputDB;
    const languageOptions = this.props.languageOptions;
    const toggleLanguage = this.props.toggleLanguage;
    const currlang = this.props.currlang;

    // Debug logging to see what props we're receiving
    // console.log("Eval component props:", {
    //   languageOptions,
    //   toggleLanguage: typeof toggleLanguage,
    //   currlang,
    //   hasLanguageOptions: !!languageOptions,
    //   hasToggleLanguage: !!toggleLanguage,
    //   hasCurrlang: !!currlang
    // });

    const conditionalRender = (): React.ReactElement => {
      if (loading) return <EliteSpinner />;

      // Treat literal "undefined" (from stale DB writes) as no output
      const hasOutput = (s: string | null | undefined) =>
        s != null && s !== "null" && s !== "undefined" && s !== "";

      // First priority: Current execution output from props (most recent)
      if (hasOutput(evalOutput)) {
        return this.renderWithScroll(evalOutput!);
      }

      // Second priority: Session storage output (from current session)
      if (hasOutput(sessionOutput)) {
        return this.renderWithScroll(sessionOutput!);
      }

      // Third priority: Database output (from previous sessions, only if no current output)
      if (hasOutput(evalOutputDB)) {
        return this.renderWithScroll(evalOutputDB!);
      }

      // If nothing is available, show default message
      return <p className="codeRes-plain">Terminal output here.</p>;
    };

    const timeRender = (): React.ReactElement | null => {
      // if (time) {
      //   return <div className="memory">Time: {time}s Memory: {memory} kb</div>
      // }
      return null;
    };

    const { darkMode } = this.props;
    const { theme } = this.state;
    // console.log("currlang: ", currlang)
    return (
      <div className="evalOut">
        {conditionalRender()}
        <div>
          {/* {timeRender()} */}
          <button
            id="runButton"
            className={`btn ${darkMode ? "letsRun-dark" : "letsRun"}`}
            onClick={this.runCodeEval}
          >
            Run
          </button>
          {/* Language dropdown positioned under Run button */}
          {languageOptions && toggleLanguage && currlang && (
            <Dropdown
              title="Language "
              list={languageOptions}
              toggleItem={(id: number, key: string) => {
                const item = languageOptions.find(lang => lang.id === id);
                if (item) {
                  toggleLanguage(id, key, item.mode);
                }
              }}
              currLang={currlang}
            />
          )}
          {/* Temporarily commented out theme toggle
          <DropdownTheme
            toggleItem={this.toggleTheme}
            title="Theme "
            theme={theme}
          />
          */}
        </div>
      </div>
    );
  }
}

export default Eval;
